import { useMemo } from 'react';
import { pipe } from 'fp-ts/function';
import { useInView } from 'react-intersection-observer';
import { WithAccountId, WithEnterpriseId } from '@mews-commander/types';
import { DefaultErrorBoundary, Loader } from '@mews-commander/ui';
import { Stack } from '@optimus-web/core';
import { lt, useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { getBillsByOwnerId } from '@mews-commander/legacy-entities/src/ducks';
import { getBillsByAssociatedAccountId } from '@mews-commander/legacy-entities/src/ducks/bills';
import { useCommanderSelector } from '@mews-commander/utils';
import { BillingMode } from '../../../constants';
import { BillCardList } from '../../../BillCardList';
import { BillListHeader, NewBillButton } from '../../../components';
import {
    filterOrderMapBills,
    getAmountDueForBillIds,
    getOrderedBillsByOwnerIdAndAssociatedId,
} from '../../../selectors';
import { AccountBillCardEmptyState } from './AccountBillCardEmptyState';
import { AccountBillCardGroup } from './AccountBillCardGroup';

export const AccountBillCard = ({ accountId, enterpriseId }: WithAccountId & WithEnterpriseId) => {
    const routingRulesSlice2Enabled = useFeatureFlag(FeatureFlag.routingRulesSlice2Enabled);

    return (
        <DefaultErrorBoundary>
            <Stack vertical spacing={routingRulesSlice2Enabled ? '300' : '200'}>
                {routingRulesSlice2Enabled ? (
                    <GroupsWrapper accountId={accountId} enterpriseId={enterpriseId} />
                ) : (
                    <OldBody accountId={accountId} enterpriseId={enterpriseId} />
                )}
            </Stack>
        </DefaultErrorBoundary>
    );
};

// TODO: remove when routingRulesSlice2Enabled feature is done
const OldBody = ({ accountId, enterpriseId }: WithAccountId & WithEnterpriseId) => {
    const billIds = useCommanderSelector((state) =>
        getOrderedBillsByOwnerIdAndAssociatedId(state, { ownerId: accountId, associatedAccountId: accountId })
    );

    const { totalAmount, billAmounts } = useCommanderSelector((state) =>
        getAmountDueForBillIds(state, { enterpriseId, billIds })
    );

    return (
        <>
            <BillListHeader
                buttons={[<NewBillButton key="NewBillButton" accountId={accountId} />]}
                amount={totalAmount}
            />

            {billIds.length === 0 ? <AccountBillCardEmptyState /> : null}

            <BillCardList
                billIds={billIds}
                billAmounts={billAmounts}
                mode={BillingMode.AccountBilling}
                accountId={accountId}
            />
        </>
    );
};

// TODO: when routingRulesSlice2Enabled feature is done. Move content of this component to parent component <AccountBillCard />
const GroupsWrapper: React.FC<WithAccountId & WithEnterpriseId> = ({ accountId, enterpriseId }) => {
    const ownedBills = useCommanderSelector((state) => getBillsByOwnerId(state, { ownerId: accountId }));

    const associatedBills = useCommanderSelector((state) =>
        getBillsByAssociatedAccountId(state, { associatedAccountId: accountId })
    );

    const { orderedOwnedBillIds, orderedAssociatedBillIds } = useMemo(
        () => ({
            orderedOwnedBillIds: pipe(ownedBills, filterOrderMapBills),
            orderedAssociatedBillIds: pipe(associatedBills, filterOrderMapBills),
        }),
        [ownedBills, associatedBills]
    );

    const showAssociatedBills = orderedAssociatedBillIds.length > 0;

    const { ref, inView } = useInView({ rootMargin: '150px', skip: !showAssociatedBills });

    return (
        <>
            <AccountBillCardGroup
                showNewButton
                showEmptyState={!orderedOwnedBillIds.length && !orderedAssociatedBillIds.length}
                title={lt('OwnedBills')}
                accountId={accountId}
                enterpriseId={enterpriseId}
                billIds={orderedOwnedBillIds}
            />

            {showAssociatedBills && (
                <div ref={ref}>
                    {inView ? (
                        <AccountBillCardGroup
                            title={lt('AssociatedBills')}
                            accountId={accountId}
                            enterpriseId={enterpriseId}
                            billIds={orderedAssociatedBillIds}
                        />
                    ) : (
                        <Loader />
                    )}
                </div>
            )}
        </>
    );
};
